import { t } from "@lingui/core/macro";
import { AlertMessage, ColumnProps } from "../../../api/types";
import DataTable from "../../dataTable";
import { Box, Pagination } from "@mui/material";
import { usePostQuery } from "../../../hooks/usePostQuery";
import TaxonomyDialog from "./taxonomyDialog";
import { useEffect, useState } from "react";
import { Apifilters, dimensionNs } from "../../../api/customObjectsTypes";

export const TaxonomyElements = ({ id, setAlertMessage, setTotal }: {
    id: string,
    setAlertMessage: (alertMessage: AlertMessage) => void
    setTotal: (total: number) => void;
}) => {

    const initReq = {
        page: 1,
        pageCount: 20,
        filters: [
            { column: "name", filterType: 1, ColumnType: 0, sortDescending: false },
            { column: "ns_href", filterType: 0, ColumnType: 0, sortDescending: false },
            { column: "periodType", filterType: 0, ColumnType: 0, sortDescending: false },
            { column: "isCredit", filterType: 0, ColumnType: 0, sortDescending: false },
        ],
        taxonomyId: id,
    };

    interface TaxonomyElementResponse {
        totalCount: number;
        result: {
            id: string;
            name: string;
            relativePath: string;
            periodType: string;
            taxonomyElementId: string;
            parserDataTypeId: number;
            taxonomyDataType: string;
            isAbstract: boolean;
            taxonomyId: string;
            rawNumber: boolean;
            ns: dimensionNs;
            entryPoints: string[];
        }[];
        filters: Apifilters[];
    }

    const [shouldFetch, setShouldFetch] = useState(true);
    const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
    const [cacheBuster] = useState(crypto.randomUUID());
    const [req, setReq] = useState(() => (initReq));

    const { data, loading } = usePostQuery<TaxonomyElementResponse>(
        `api/taxonomies/${id}/elements/search?cb=${cacheBuster}`,
        req,
        !shouldFetch
    );

    useEffect(() => {
        if (data) {
            setTotal(data.totalCount);
        }
    }, [data, setTotal]);

    useEffect(() => {
        if (shouldFetch) {
            setShouldFetch(false);
        }
    }, [shouldFetch]);

    const Tablecolumns: () => ColumnProps[] = () => {
        return [
            {
                header: {
                    id: "name",
                    label: t`Name`,
                    optionsHeaderText: t`Name`,
                    shouldBeFiltered: true,
                    inputType: "text",
                    width: 600,
                },
                key: "name",
                width: 600,
            },
            {
                header: {
                    id: "ns_href",
                    label: t`Name Space`,
                    shouldBeFiltered: true,
                    optionsHeaderText: t`Select Name Space`,
                    inputType: "select",
                    width: 250,
                },
                valueFormatter: (row: any) => row?.ns?.href || "",
                key: "ns_href",
                width: 250,
            },
            {
                header: {
                    id: "periodType",
                    label: t`Period`,
                    shouldBeFiltered: true,
                    inputType: "select",
                    optionsHeaderText: t`Period Type`,
                    width: 100,
                },
                key: "periodType",
                width: 100,
            },
            {
                header: {
                    id: "isCredit",
                    label: t`Is Credit`,
                    shouldBeFiltered: true,
                    inputType: "select",
                    width: 250,
                },
                valueFormatter: (row: any) => row?.isCredit ? t`Yes` : t`No`,
                key: "isCredit",
                width: 250,
            },
        ];
    };

    const handleSetFilter = (columnId: string, value?: string | boolean | number | null, sortDescending?: boolean, enterPressed?: boolean) => {
        if (columnId === "page") {
            setReq((prevReq: any) => {
                return {
                    ...prevReq,
                    page: value,
                };
            });
        } else {

            setReq((prevReq: any) => {
                return {
                    ...prevReq,
                    filters: prevReq.filters.map((filter: any) => {
                        if (filter.column === columnId) {
                            return { ...filter, value: value === "null" ? null : value === "empty" ? "" : value, sortDescending };
                        }
                        return filter;
                    }),
                };
            });
        }
        if (enterPressed) {
            setShouldFetch(true);
        }
    };

    useEffect(() => {
        const hasSortDescendingChanged = req.filters.some((filter: Apifilters) => filter.sortDescending !== undefined);
        if (hasSortDescendingChanged) {
            setReq((prevReq: any) => {
                const newFilters = prevReq.filters.map((filter: Apifilters) => ({
                    ...filter,
                    sortDescending: filter.sortDescending,
                }));
                const isSame = JSON.stringify(prevReq.filters) === JSON.stringify(newFilters);
                if (isSame) return prevReq;
                return {
                    ...prevReq,
                    filters: newFilters,
                    page: 1,
                };
            });
        }
    }, [req.filters]);

    return (
        <>
            <Box sx={{ mt: 5, alignContent: "center", display: "flex", flexDirection: "column" }}>
                <DataTable
                    data={data?.result || []}
                    loading={loading}
                    filters={data?.filters || []}
                    setFilter={handleSetFilter}
                    columns={Tablecolumns()}
                    noWidth
                    noDataMessage={t`No Taxonomy Elements`}
                    onClickRow={(row) => {
                        setSelectedId(row);
                    }}
                />
                {data && data?.totalCount > 20 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
                        <Pagination
                            count={Math.ceil(data.totalCount / 20)}
                            page={req.page}
                            onChange={(event, value) => handleSetFilter("page", value, undefined, true)}
                            color="primary"
                        />
                    </Box>
                )}
            </Box>
            <TaxonomyDialog
                selectTemplate="taxonomyElements"
                open={!!selectedId}
                onClose={(updateStatus, reason) => {
                    setSelectedId(undefined);
                    if (updateStatus) {
                        setAlertMessage({ message: reason || "", severity: updateStatus });
                        setShouldFetch(true);
                    }
                }}
                id={selectedId || ""}
                parentId={id}
            />
        </>
    );
};